import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import CreativeInsightsService from 'services/CreativeInsightsService';
import TaggingStatus, { isRunning } from './TaggingStatus';
import VocabularyPanel from './VocabularyPanel';
import CreativeTagsTable from './CreativeTagsTable';
import {
  InsightsStatus,
  JobName,
  TaggedCreative,
  TagField,
  VocabValue,
} from './types';

// How often to re-check a running job. Jobs take minutes; the only thing this
// buys is the progress count moving. Polls /jobs (the queue, no warehouse
// query); /status is reloaded once the job finishes.
const POLL_MS = 15000;

// The Tags tab: AI tagging of every creative against shared persona and
// selling-point lists that a person approves. Stage 1 of Creative Performance V2;
// the Insights tab (performance by tag) is built on what this produces.
export default function TagsTab() {
  const service = useMemo(() => new CreativeInsightsService(), []);
  const [status, setStatus] = useState<InsightsStatus | null>(null);
  const [vocabulary, setVocabulary] = useState<VocabValue[]>([]);
  const [creatives, setCreatives] = useState<TaggedCreative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<JobName | null>(null);
  const wasRunning = useRef(false);

  const loadAll = useCallback(async () => {
    const [s, v, c] = await Promise.all([
      service.getStatus(),
      service.getVocabulary(),
      service.getCreatives(),
    ]);
    // Show what did load: an empty list in place of one that failed would
    // read as "nothing here yet" and invite duplicate values.
    if (s) setStatus(s);
    if (v) setVocabulary(v.values);
    if (c) setCreatives(c.rows);
    const failed = [
      !s && 'tagging status',
      !v && 'tag lists',
      !c && 'tagged creatives',
    ].filter(Boolean);
    setError(
      failed.length
        ? `Could not load ${failed.join(', ')}. Check the API is running and try reloading.`
        : null,
    );
    setLoading(false);
  }, [service]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // While a job runs, poll the queue; when it finishes, reload everything so
  // new tags and proposed values appear without a manual refresh.
  const anyRunning = status
    ? Object.values(status.jobs).some(isRunning)
    : false;
  useEffect(() => {
    if (!anyRunning) {
      if (wasRunning.current) {
        wasRunning.current = false;
        loadAll();
      }
      return undefined;
    }
    wasRunning.current = true;
    const timer = setInterval(async () => {
      const j = await service.getJobs();
      if (j) setStatus(prev => prev && { ...prev, jobs: j.jobs });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [anyRunning, service, loadAll]);

  const startJob = async (job: JobName, limit?: number) => {
    setStarting(job);
    try {
      await service.startJob(job, limit);
      const j = await service.getJobs();
      if (j) setStatus(prev => prev && { ...prev, jobs: j.jobs });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setStarting(null);
    }
  };

  const reloadVocabulary = async () => {
    const v = await service.getVocabulary();
    if (v) setVocabulary(v.values);
  };

  const updateValue = async (
    valueId: string,
    patch: Parameters<CreativeInsightsService['updateValue']>[1],
  ) => {
    try {
      await service.updateValue(valueId, patch);
      await reloadVocabulary();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const addValue = async (
    value: Parameters<CreativeInsightsService['addValue']>[0],
  ) => {
    await service.addValue(value);
    await reloadVocabulary();
  };

  const setTag = async (
    creativeKey: string,
    field: TagField,
    value: string | string[] | null,
  ) => {
    await service.setTag(creativeKey, field, value);
    const c = await service.getCreatives();
    if (c) setCreatives(c.rows);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: '#64748B', mb: 2, maxWidth: 900 }}
      >
        The AI watches each creative once (video and sound, or the image; never
        the headline or caption, since those rotate) and notes who it's for and
        what it claims. Those notes become shared persona and selling-point
        lists that you approve, and every creative is then tagged from those
        lists only, plus hook, angle, format, presenter, offer and awareness.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <TaggingStatus status={status} onStart={startJob} starting={starting} />
      <VocabularyPanel
        values={vocabulary}
        products={status?.products || []}
        onUpdate={updateValue}
        onAdd={addValue}
      />
      <CreativeTagsTable
        rows={creatives}
        vocabulary={vocabulary}
        onSetTag={setTag}
      />
    </Box>
  );
}
